/**
 * createJob.ts
 *
 * Pure client-side logic for the Title Search (TR) job-creation form:
 * the effective-date window, form-state validation, and the job-creation payload builder.
 *
 * All functions here are side-effect free so they can be unit/property tested
 * in isolation (see task 18.2). The React form component
 * (`CreateTitleSearchJobModal`) and the GraphQL service consume these helpers.
 *
 * The client-side rules mirror the backend `CreateTitleJobInput` validation so
 * the user gets immediate feedback; the backend remains the source of truth and
 * re-validates on submit.
 *
 * Requirements: 1.1, 1.2, 1.3, 1.5, 1.6, 1.7, 1.8, 1.9, 9.7
 */

/**
 * The fixed job type for every Title Search job (Requirement 1.2). The backend
 * `JobType` enum exposes this as `TR` (Title Report).
 */
export const TITLE_SEARCH_JOB_TYPE = 'TR' as const;

/** Maximum field lengths, mirroring the backend `CreateTitleJobInput` model. */
export const TITLE_SEARCH_FIELD_MAX_LENGTHS = {
  loanNumber: 50,
  clientName: 200,
  borrowerName: 200,
  propertyAddress: 500,
  propertyCounty: 100,
  propertyState: 100,
} as const;

/** The earliest allowed effective date (Requirement 1.9), ISO 8601. */
export const EFFECTIVE_DATE_MIN = '1900-01-01';

/** The effective date may be at most this many days in the future (1.9). */
export const EFFECTIVE_DATE_MAX_DAYS_AHEAD = 90;

/**
 * The editable state of the Title Search creation form. Every field is a string
 * so it maps directly onto controlled `<input>` elements; numeric/date coercion
 * happens at validation and submit time.
 */
export interface TitleSearchJobFormState {
  loanNumber: string;
  clientName: string;
  borrowerName: string;
  effectiveDate: string; // ISO 8601 (YYYY-MM-DD) from <input type="date">
  propertyAddress: string;
  propertyCounty: string;
  propertyState: string;
}

/** A blank form state, used to initialize / reset the form. */
export const EMPTY_TITLE_SEARCH_FORM: TitleSearchJobFormState = {
  loanNumber: '',
  clientName: '',
  borrowerName: '',
  effectiveDate: '',
  propertyAddress: '',
  propertyCounty: '',
  propertyState: '',
};

/**
 * Per-field validation errors. A field is present in the map only when it has an
 * error, so an empty object means the form is valid.
 */
export type TitleSearchFormErrors = Partial<
  Record<keyof TitleSearchJobFormState, string>
>;

/**
 * The job-creation payload sent to the backend for a Title Search job.
 *
 * Carries every entered field unchanged plus the fixed `jobType` (1.2, 1.3).
 * Defined locally (rather than reusing the shared `CreateJobInput`) so this
 * task stays self-contained; the backend GraphQL `CreateJobInput` accepts these
 * optional fields for `TR` jobs.
 */
export interface CreateTitleJobInput {
  name?: string;
  jobType: typeof TITLE_SEARCH_JOB_TYPE;
  loanNumber: string;
  clientName: string;
  borrowerName: string;
  effectiveDate: string;
  propertyAddress: string;
  propertyCounty: string;
  propertyState: string;
}

/** Pad a number to two digits (used for date formatting). */
function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * Format a `Date` as an ISO 8601 calendar date (YYYY-MM-DD) using its UTC parts.
 * The form works purely with calendar dates, so UTC parts avoid timezone drift.
 */
export function toISODate(date: Date): string {
  return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(
    date.getUTCDate()
  )}`;
}

/**
 * Parse an ISO 8601 calendar-date string into a UTC `Date`, returning `null`
 * when the string is not a real calendar date (e.g. `2023-02-30`, `2023-13-01`,
 * or a malformed string). This mirrors the backend's strict date parsing.
 */
export function parseISODate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  // Reject values that JS "rolled over" (e.g. Feb 30 -> Mar 2).
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return date;
}

/**
 * The inclusive effective-date window `[1900-01-01, today + 90 days]`
 * (Requirement 1.9), expressed as ISO 8601 strings suitable for an
 * `<input type="date">` `min` / `max` and for lexicographic comparison.
 *
 * @param today Reference "now"; defaults to the current date. Injectable for
 *   deterministic tests.
 */
export function effectiveDateBounds(today: Date = new Date()): {
  min: string;
  max: string;
} {
  const maxDate = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate())
  );
  maxDate.setUTCDate(maxDate.getUTCDate() + EFFECTIVE_DATE_MAX_DAYS_AHEAD);
  return { min: EFFECTIVE_DATE_MIN, max: toISODate(maxDate) };
}

/**
 * True when `value` is a valid calendar date within the effective-date window.
 * ISO 8601 date strings compare correctly with lexicographic ordering, so the
 * bound check is a simple string comparison once validity is established.
 */
export function isWithinEffectiveDateWindow(
  value: string,
  today: Date = new Date()
): boolean {
  if (parseISODate(value) === null) {
    return false;
  }
  const { min, max } = effectiveDateBounds(today);
  return value >= min && value <= max;
}

/** Format an ISO date as MM/DD/YYYY for human-readable error messages. */
function formatHumanDate(isoDate: string): string {
  const parsed = parseISODate(isoDate);
  if (!parsed) {
    return isoDate;
  }
  return `${pad2(parsed.getUTCMonth() + 1)}/${pad2(
    parsed.getUTCDate()
  )}/${parsed.getUTCFullYear()}`;
}

interface ValidateOptions {
  /** Reference "now" for the effective-date window; defaults to current date. */
  today?: Date;
}

/**
 * Validate the Title Search creation form, returning a per-field error map.
 * An empty map means the form is valid and may be submitted.
 *
 * Rules mirror the backend `CreateTitleJobInput` (Requirement 1, 9.7):
 * - All seven fields are required and must be non-blank.
 * - Text fields must not exceed their max length.
 * - Property county and state are free text.
 * - Effective date must be a valid calendar date in `[1900-01-01, today+90]`
 *   (1.9).
 */
export function validateTitleSearchJobForm(
  state: TitleSearchJobFormState,
  options: ValidateOptions = {}
): TitleSearchFormErrors {
  const today = options.today ?? new Date();
  const errors: TitleSearchFormErrors = {};

  // Loan number (required, max 50) — Requirement 9.7.
  if (!state.loanNumber.trim()) {
    errors.loanNumber = 'Loan number is required';
  } else if (state.loanNumber.trim().length > TITLE_SEARCH_FIELD_MAX_LENGTHS.loanNumber) {
    errors.loanNumber = `Loan number must be ${TITLE_SEARCH_FIELD_MAX_LENGTHS.loanNumber} characters or less`;
  }

  // Client name (required, max 200) — Requirement 9.7.
  if (!state.clientName.trim()) {
    errors.clientName = 'Client name is required';
  } else if (state.clientName.trim().length > TITLE_SEARCH_FIELD_MAX_LENGTHS.clientName) {
    errors.clientName = `Client name must be ${TITLE_SEARCH_FIELD_MAX_LENGTHS.clientName} characters or less`;
  }

  // Borrower's name (required, max 200) — Requirement 1.8.
  if (!state.borrowerName.trim()) {
    errors.borrowerName = "Borrower's name is required";
  } else if (state.borrowerName.trim().length > TITLE_SEARCH_FIELD_MAX_LENGTHS.borrowerName) {
    errors.borrowerName = `Borrower's name must be ${TITLE_SEARCH_FIELD_MAX_LENGTHS.borrowerName} characters or less`;
  }

  // Property address (required, max 500) — Requirement 1.5.
  if (!state.propertyAddress.trim()) {
    errors.propertyAddress = 'Property address is required';
  } else if (
    state.propertyAddress.trim().length > TITLE_SEARCH_FIELD_MAX_LENGTHS.propertyAddress
  ) {
    errors.propertyAddress = `Property address must be ${TITLE_SEARCH_FIELD_MAX_LENGTHS.propertyAddress} characters or less`;
  }

  // Property county (required, max 100) — Requirement 1.6.
  if (!state.propertyCounty.trim()) {
    errors.propertyCounty = 'Property county is required';
  } else if (
    state.propertyCounty.trim().length > TITLE_SEARCH_FIELD_MAX_LENGTHS.propertyCounty
  ) {
    errors.propertyCounty = `Property county must be ${TITLE_SEARCH_FIELD_MAX_LENGTHS.propertyCounty} characters or less`;
  }

  // Property state (required, max 100).
  if (!state.propertyState.trim()) {
    errors.propertyState = 'Property state is required';
  } else if (
    state.propertyState.trim().length > TITLE_SEARCH_FIELD_MAX_LENGTHS.propertyState
  ) {
    errors.propertyState = `Property state must be ${TITLE_SEARCH_FIELD_MAX_LENGTHS.propertyState} characters or less`;
  }

  // Effective date (required + valid window) — Requirement 1.9.
  if (!state.effectiveDate.trim()) {
    errors.effectiveDate = 'Effective date is required';
  } else if (parseISODate(state.effectiveDate) === null) {
    errors.effectiveDate = 'Effective date must be a valid calendar date';
  } else if (!isWithinEffectiveDateWindow(state.effectiveDate, today)) {
    const { max } = effectiveDateBounds(today);
    errors.effectiveDate = `Effective date must be between ${formatHumanDate(
      EFFECTIVE_DATE_MIN
    )} and ${formatHumanDate(max)}`;
  }

  return errors;
}

/** True when the form has no validation errors. */
export function isTitleSearchJobFormValid(
  state: TitleSearchJobFormState,
  options: ValidateOptions = {}
): boolean {
  return Object.keys(validateTitleSearchJobForm(state, options)).length === 0;
}

/**
 * Build the job-creation payload from the form state, carrying every entered
 * field through unchanged and stamping the fixed `TR` job type (1.2, 1.3).
 *
 * @param state The validated form state.
 * @param name Optional human-friendly job name; defaults to a name derived from
 *   the client and loan number.
 */
export function buildCreateTitleJobPayload(
  state: TitleSearchJobFormState,
  name?: string
): CreateTitleJobInput {
  return {
    name: name ?? defaultTitleSearchJobName(state),
    jobType: TITLE_SEARCH_JOB_TYPE,
    loanNumber: state.loanNumber,
    clientName: state.clientName,
    borrowerName: state.borrowerName,
    effectiveDate: state.effectiveDate,
    propertyAddress: state.propertyAddress,
    propertyCounty: state.propertyCounty,
    propertyState: state.propertyState,
  };
}

/** Derive a default, human-readable job name from the entered details. */
export function defaultTitleSearchJobName(state: TitleSearchJobFormState): string {
  const client = state.clientName.trim();
  const loan = state.loanNumber.trim();
  if (client && loan) {
    return `Title Search - ${client} (${loan})`;
  }
  if (client) {
    return `Title Search - ${client}`;
  }
  if (loan) {
    return `Title Search - ${loan}`;
  }
  return 'Title Search Job';
}
